<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick, computed } from 'vue'
import DataTable from './datatable/DataTable.vue'
import SplitDataTable from './datatable/SplitDataTable.vue'
import Comments from './Comments.vue'
import HorizontalSplitter from './HorizontalSplitter.vue'
import RechtsaspektSelect from './RechtsaspektSelect.vue'

import { Button } from "@/components/ui/button"
import { NotebookPen, ClipboardCheck, Loader2 } from "lucide-vue-next"
import { get_data_attr, sendNotification, applyMatrixChange } from '@/utils/globals'
import rkIconUrl from '@/components/images/rk.svg?url'
import { useI18n } from 'vue-i18n'
import axios from "axios"
import { useToast} from "@/components/ui/toast"
import { useViewStore } from '@/stores/viewStore'
import { hasGlobalAssignmentAccess } from '@/utils/assignmentRoles'
import RechtsgrundlagenLogo from '@/components/images/rechtsgrundlagen.svg'
import PflichtenLogo from '@/components/images/pflichten.svg'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectLabel,
  SelectItem
} from '@/components/ui/select'

const { toast } = useToast()
const { t } = useI18n()
const customerInfo = get_data_attr("customer");
const userInfo = get_data_attr("user");
const viewStore = useViewStore();
viewStore.hydrateAssignmentScopeMask(userInfo?.assignment_scope_mask);

const selectedTaskId = ref(null)
const selectedTask = ref(null)
const selectedComments = ref([])
const selectedRechtsgrundlagen = ref([])
const selectedPflichten = ref([])

// props for parent events
const props = defineProps({
  specsInfo: { type: Object },
});

// --- Splitter + width logic ---

const splitterRef = ref<InstanceType<typeof HorizontalSplitter>>(null);
const showComments = ref(false);
const showRechtsgrundlagen = ref(false);
const showPflichten = ref(false);

const emit = defineEmits<{
  (e: 'swapcol', endpoint: string, from: number, to: number): void;
}>();

function setSelected(row, detailtype) {
  const clickedComment = detailtype === 0
  const clickedRechtsgrundlagen = detailtype === 1
  const clickedPflichten = detailtype === 2

  const unChangedContext =
    selectedTaskId.value === row.id &&
    (
      (clickedComment && showComments.value) ||
      (clickedRechtsgrundlagen && showRechtsgrundlagen.value) ||
      (clickedPflichten && showPflichten.value)
    )

  // Toggle close if same row clicked again
  if (unChangedContext) {
    splitterRef.value.showRight = !splitterRef.value.showRight
  } else {
    // New row — show right panel
    selectedTaskId.value = row.id
    selectedTask.value = row
    selectedComments.value = row.comments
    selectedRechtsgrundlagen.value = row.rechtsgrundlagenmatrix
    selectedPflichten.value = row.pflichtenmatrix

    splitterRef.value.showRight = true

    showComments.value = clickedComment
    showRechtsgrundlagen.value = clickedRechtsgrundlagen
    showPflichten.value = clickedPflichten
  }

  splitterRef.value?.setSelected(row)
}

// --- Add comment handler ---
async function addComment(comment) {
  try {
    comment.task_id = selectedTask.value.id
    await axios.post(`/comments`, comment)
    toast({ title: "Kommentar", description: "eingetragen" })
    selectedComments.value.push(comment)
  } catch (error) {
    const message =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.response?.statusText ||
      "Kommentar nicht eingetragen."
    toast({ title: "Kommentar nicht eingetragen", description: message, variant: "destructive" })
  }
}

const rg_cols = [{key: "Rechtsgrundlage _Kürzel_", label: 'Rechtsgrundlagekürzel', show: true, write: false, width: '160px'},
                 ]

/** Full Rechtsgrundlagen field list for read-only detail modal (glasses). */
const rgReadonlyDetailColumns = computed(() => {
  const s = props.specsInfo?.rechtsgrundlagen
  if (!s || !Array.isArray(s)) return []
  return s.map((col) => ({ ...col, write: false }))
})
const pf_cols = [
                 {key: "Rechtsgrundlage _Kürzel_", label: 'Rechtsgrundlagekürzel', show: true, write: false, width: '160px'},
				 {key: "Anforderung", label: 'Anforderung', show: true, write: false, width: '160px'},
				 {key: "Referenz", label: 'Referenz', show: true, write: false, width: '160px'},
                ]
				
var last_rgshort = null;
var last_rechtsaspekt = null;
function getReferenz(event) {
	const select = document.getElementById('referenz');
	let rgshort = document.getElementById("rgshort0").value.trim();
	if (rgshort === null || rgshort === "") {
        return;
	}
	
	let rechtsaspekt = document.getElementById("rechtsaspekt").value.trim();

	if (rechtsaspekt === null || rechtsaspekt === "") {
	    return;
	}
	
	if (rgshort === last_rgshort && rechtsaspekt === last_rechtsaspekt) {
		return;
	}
	
	let pars = {"rgshort" : rgshort, "rechtsaspekt": rechtsaspekt};
	sendRequest('/pflichten/referenz', pars, function(data) {
		select.innerHTML = '<option value="">Wählen</option>'; // Reset options

		// Add new options dynamically
		data.forEach(item => {
		  const option = document.createElement('option');
		  option.value = item.label;
		  option.textContent = item.value;
		  option.title = item.requirement;
		  select.appendChild(option);
		});
		last_rgshort = rgshort;
		last_rechtsaspekt = rechtsaspekt;
	},
	function() {});				
}

const rechtsaspektSelect = ref();
const selectedRechtsaspekt = ref("")
const selectedRgShort = ref("")
const referenzSelect = ref()
const referenzOptions = ref([]) // options fetched from server
const loading = ref(false)    // optional loading state

// Watch for changes in firstSelect or textInput
watch([selectedRechtsaspekt, selectedRgShort], async ([newRechtsaspekt, newRgShort]) => {
  if (!newRechtsaspekt) {
    referenzOptions.value = []
    referenzSelect.value = ''
    return
  }
  if (!String(newRgShort ?? '').trim()) {
    referenzOptions.value = []
    referenzSelect.value = ''
    return
  }

  loading.value = true
  try {
	let pars = {"rechtsaspekt": newRechtsaspekt, "rgshort" : newRgShort};
    const { data } = await axios.get('/referenz', {params: pars})
    // assume server returns array of { value: string, label: string }
	console.log("referenz list=" + JSON.stringify(data))
    referenzOptions.value = [...data]
	referenzSelect.value = ''
  } catch (err) {
    console.error('Failed to fetch options:', err)
    referenzOptions.value = []
    referenzSelect.value = ''
  } finally {
    loading.value = false
  }
}, { immediate: true })


const addLegalToTask = async (rtype, task_id, payload, onComplete) => {
	payload["rgshort"] =  selectedRgShort.value
	payload["task_id"] =  task_id
	try {
	  let url = "/" + rtype + "matrix_tasks.json";
	  const {data} = await axios.post(url, payload)
	  console.log('Server response:', data)

	  toast({ title: rtype, description: "hinzugefügt" })
	  
	  onComplete(data);
	} catch (err) {
	  console.error('Failed to submit:', err)
	  toast({ title: rtype, description: "nicht hinzugefügt" + err.response?.data?.error, variant: "destructive" })
	  // optionally show error to user
	} finally {
	}
}

const addRgToTask = async (task_id) => {
	const payload = {}
	addLegalToTask("rechtsgrundlagen", task_id, payload, function (data) {
	  selectedRechtsgrundlagen.value = data
	})
}
	
const addPfToTask = async (task_id) => {
	if (!referenzSelect.value) return

	const payload = { rechtsaspekt: selectedRechtsaspekt.value, referenz: referenzSelect.value }
	
	addLegalToTask("pflichten", task_id, payload, function (data) {
	  selectedPflichten.value = data
	})
}

const deleteTaskLegal = async (rtype, id, onComplete) => {
  try {
	const {data} = await axios.delete("/" + rtype + "matrix_tasks/" + id + ".json")
	onComplete(data)
	toast({ title: rtype, description: "gelöscht" })
  }	catch (err) {
	console.error('Failed to submit:', err)
	toast({ title: rtype, description: "nicht gelöscht" + err.message, variant: "destructive" })
	  // optionally show error to user
  } finally {
  }	
}

const deleteTaskRechtsgrundlagen = async (id) => {
  deleteTaskLegal("rechtsgrundlagen", id, function(data) {
	selectedRechtsgrundlagen.value = data	
  })
}

const deleteTaskPflicht = async (id) => {
  deleteTaskLegal("pflichten", id, function(data) {
	selectedPflichten.value = data	
  })
}

const datafunctions = ref([])
const datausers = ref([])

const taskDataTable = ref<any>(null)
const splitTaskRef = ref<InstanceType<typeof SplitDataTable>>(null);
const splitTaskFunction   = ref(false)
const splitTaskUser       = ref(false)
const rightTaskData = ref<any[]>([]);

function openTaskFunctions() {
  splitTaskFunction.value = splitTaskUser.value || !splitTaskFunction.value 
  splitTaskUser.value = false;
  splitTaskRef.value?.toggleRight(splitTaskFunction.value)
}

function openTaskUsers() {
  splitTaskUser.value = splitTaskFunction.value || !splitTaskUser.value
  splitTaskFunction.value = false;
  splitTaskRef.value?.toggleRight(splitTaskUser.value)
}

const rightTaskTableLabel = computed(() => {
  return splitTaskFunction.value ? "view.function_assignment" : "view.user_assignment"
});

function assignmentTableKey(base: string) {
  return `${base}-${viewStore.assignmentScopeMask ?? 'unset'}-${viewStore.refreshKey}`
}

function assignmentColumnStateKey(base: string) {
  return viewStore.assignmentScopeMode('tasks') === 'own' ? `${base}-own` : base
}

const ownFunctionColumnKeys = computed(() => {
  const fids = userInfo.function_ids || []
  return fids.map((id: number) => "assignment." + id)
})

const ownUserColumnKeys = computed(() => {
  return ["assignment_users." + userInfo.id]
})

const userAssignmentRows = computed(() => {
  const userIds = datausers.value.map((u: any) => String(u.id))
  return rightTaskData.value.map((row: any) => {
    const assignmentUsers = { ...(row?.assignment_users || {}) }
    userIds.forEach((uid) => {
      if (!(uid in assignmentUsers)) assignmentUsers[uid] = null
    })
    return { ...row, assignment_users: assignmentUsers }
  })
})

const functionTableColumns = computed(() => {
  const type = "tasks"
  const ownSet = new Set(ownFunctionColumnKeys.value)
  const defaultShow = ownSet.size === 0
  const isAssignee = userInfo.is_assignee && ownSet.size > 0
  const hasGlobalAssignmentWrite = hasGlobalAssignmentAccess(userInfo)
  const hasGlobalAssignmentRead = hasGlobalAssignmentWrite
  const scopeOwnOnly = viewStore.isAssignmentOwn('tasks') === true && ownSet.size > 0
  const scopeShowAll = viewStore.isAssignmentOwn('tasks') === false
  return datafunctions.value.map(f => {
    const key = "assignment." + f.id
    const baseCanWrite = hasGlobalAssignmentWrite || ownSet.has(key)
    const canWrite = scopeOwnOnly ? ownSet.has(key) : baseCanWrite
    const canShow = scopeShowAll || (scopeOwnOnly ? ownSet.has(key) : (hasGlobalAssignmentRead || defaultShow || ownSet.has(key)))
    return {key, label: f.functionname + "(" + f.id + ")", type: 'select', togglefilter: true, show: canShow, write: canWrite, options:status_options(type), width: '160px'}
  }).sort((a, b) => a.label.localeCompare(b.label))
})

const userTableColumns = computed(() => {
  const type = "tasks"
  const ownSet = new Set(ownUserColumnKeys.value)
  const defaultShow = ownSet.size === 0
  const assignedSet = new Set<string>()
  const isAssignee = userInfo.is_assignee && ownSet.size > 0
  const hasGlobalAssignmentWrite = hasGlobalAssignmentAccess(userInfo)
  const hasGlobalAssignmentRead = hasGlobalAssignmentWrite
  const scopeOwnOnly = viewStore.isAssignmentOwn('tasks') === true && ownSet.size > 0
  const scopeShowAll = viewStore.isAssignmentOwn('tasks') === false
  return datausers.value.map(u => {
    const key = "assignment_users." + u.id
    const baseCanWrite = hasGlobalAssignmentWrite || ownSet.has(key)
    const canWrite = scopeOwnOnly ? ownSet.has(key) : baseCanWrite
    const canShow = scopeShowAll || (scopeOwnOnly ? ownSet.has(key) : (hasGlobalAssignmentRead || defaultShow || ownSet.has(key) || assignedSet.has(key)))
    return {key, label: u.firstname + ' ' + u.lastname, type: 'select', togglefilter: true, show: canShow, write: canWrite, options:status_options(type), width: '160px'}
  }).sort((a, b) => a.label.localeCompare(b.label))
})

async function onMatrixCellChanged(change, isByFunction) {
  const row = rightTaskData.value.find((r: any) => r.id === change.id)
  if (row) {
    const parts = String(change.path).split('.')
    if (parts.length === 2) {
      if (!row[parts[0]]) row[parts[0]] = {}
      row[parts[0]][parts[1]] = change.value
    } else {
      row[parts[0]] = change.value
    }
    rightTaskData.value = [...rightTaskData.value]
  }
  const saved = await applyMatrixChange(axios, toast, customerInfo, 'tasks', change, isByFunction)
  if (saved) await taskDataTable.value?.reloadData?.()
}

function status_options(type) {
	const colors_status = get_data_attr("colors_status_" + type);
	return get_data_attr("choice_status_" + type).map(status => ({id: status[1], label: status[0], bgcolor: colors_status[status[1]]}));
}

async function getDataUrl(url) {
  try {
    const response = await fetch(url);
    const data = await response.json();
    console.log('Received data!', data);
    return data;
  } catch (error) {
    console.error('Error fetching data!', error);
    return null;
  }
}

async function getData(category, all=false): Promise<Project[]> {
  return getDataUrl('/' + category + '.json' + (all ? "?per_page=10000" : ""));
}


async function reload() {
  datafunctions.value = await getData('functions', true)
  datausers.value = await getData('users', true)
}
onMounted(() => reload());

function swapcol(endpoint, from, to) {
	emit('swapcol', endpoint, from, to)
}

</script>

<template>
  <HorizontalSplitter ref="splitterRef">
	<template #leftcontent>

		<SplitDataTable  ref="splitTaskRef">
		   <template #left>
		<DataTable
		  ref="taskDataTable"
		  label="view.tasks"
		  labelSingular="view.task"
		  endpoint="tasks"
		  assignmentScopeArea="tasks"
		  :columns="props.specsInfo['tasks']"
		  :enableAdd="userInfo?.is_hoeppner || userInfo?.is_assigner"
		  :enableEdit="false"
		  @swapcol="swapcol"
		  @page-loaded="rightTaskData = $event"
		>
		  <template #logo><ClipboardCheck /></template>

		  <template #extraglobalactions>
		    <button
		      @click="openTaskFunctions()"
		      class="greenbutton text-white rounded w-60 h-9"
		    >
		      {{ (splitTaskFunction ? 'Verstecke ' : 'Bearbeite ') + "Funktionszuweisung" }}
		    </button>

		    <button
		      @click="openTaskUsers()"
		      class="greenbutton text-white  rounded w-60 h-9"
		    >
		      {{ (splitTaskUser ? 'Verstecke ' : 'Bearbeite ') + "Personenzuweisung" }}
		    </button>
		  </template>

		  <!-- Each row’s NotebookPen button -->
		  <template #extraactions="{ row }">
		    <Button
		      :id="'comments-' + row.id"
		      :title="row.comments.length + ' ' + t('view.btn_comments')"
		      size="icon"
		      variant="ghost"
		      :class="[
		        row.comments.length > 0 ? 'text-green-600' : 'text-black-600',
		        'hover:bg-blue-50'
		      ]"
		      @click="() => setSelected(row, 0)"
		    >
		      <NotebookPen class="w-4 h-4" />
		    </Button>
		  <Button
		    :id="'rechtsgrundlagen-' + row.id"
		    :title="row.rechtsgrundlagenmatrix.length + ' ' + t('view.btn_rechtsgrundlagen')"
		    size="icon"
		    variant="ghost"
		    :class="[row.rechtsgrundlagenmatrix.length > 0 ? 'text-green-600' : 'text-black-600','hover:bg-blue-50']"
		    @click="() => setSelected(row, 1)"
		  >
		    <RechtsgrundlagenLogo class="w-4 h-4" />
		  </Button>
		    <Button
		      v-if="customerInfo?.is_premium"
		      :id="'pflichten-' + row.id"
		      :title="row.pflichtenmatrix.length + ' ' + t('view.btn_pflichten')"
		      size="icon"
		      variant="ghost"
		      :class="[row.pflichtenmatrix.length > 0 ? 'text-green-600' : 'text-black-600','hover:bg-blue-50']"
		      @click="() => setSelected(row, 2)"
		     >
		     <PflichtenLogo class="w-4 h-4" />
		    </Button>
		  </template>
		</DataTable>
		  	</template>
		     <template #right>
				<DataTable v-if="splitTaskFunction" :key="assignmentTableKey('func-assignment')" :label="rightTaskTableLabel"
				 :logo="rkIconUrl" 
				 :data="rightTaskData"
				 :columns="functionTableColumns"
				 :columnStateKey="assignmentColumnStateKey('tasks-function-assignment')"
				 :ownColumnKeys="ownFunctionColumnKeys"
				   @cell-changed="(change) => onMatrixCellChanged(change, true)"
				   @cell-notified="(change) => sendNotification(axios, toast, customerInfo, 'tasks', change, true)"
				  :enableAdd="false"
				  :enableEdit="false"
				  :enableDetail="false"
				  :enableDelete="false"
				  :enableFilter="false"
				  :enableColChange="false"
				  :alwaysEdit="true"
				  :headerFontSize="12"
				  :singleSelectFontsize="12"
				  :enableColFilterSide="false"
				  :enableColFilterTop="true"
				  :enableCopy="false"
				  :enableNotifications="true"
				  :hideActionColumn="true"
				  :withpaging="false"
				  :enableAll="true">
				  <template #logo><ClipboardCheck class="w-8 h-8" /></template>
				</DataTable>

				<DataTable v-if="!splitTaskFunction" :key="assignmentTableKey('user-assignment')" :label="rightTaskTableLabel"
				 :logo="rkIconUrl" 
				 :data="userAssignmentRows"
				 :columns="userTableColumns"
				 :columnStateKey="assignmentColumnStateKey('tasks-user-assignment')"
				 :ownColumnKeys="ownUserColumnKeys"
		  	     @cell-changed="(change) => onMatrixCellChanged(change, false)"
				 @cell-notified="(change) => sendNotification(axios, toast, customerInfo, 'tasks', change, false)"
		  	    :enableAdd="false"
		  	    :enableEdit="false"
		  	    :enableDetail="false"
		  	    :enableDelete="false"
		  	    :enableFilter="false"
		  	    :enableColChange="false"
		  		:alwaysEdit="true"
		  	    :headerFontSize="12"
		  	    :singleSelectFontsize="12"
		  	    :enableColFilterSide="false"
				:enableColFilterTop="true"
				:enableCopy="false"
				:enableNotifications="true"
		  	    :hideActionColumn="true"
		  		:withpaging="false"
				:enableAll="true">
		          <template #logo><ClipboardCheck class="w-8 h-8" /></template>
		        </DataTable>
		    </template>
		  </SplitDataTable>

	</template>
    <template #rightcontent={row}>
		<Comments v-if="showComments"
		  :disabled="selectedTask == null"
		  :comments="selectedComments"
		  @add-comment="(comment) => addComment(comment)"
		/>

		<div v-if="showRechtsgrundlagen">
		  <DataTable v-if="showRechtsgrundlagen" :label="`Rechtsgrundlagen für ${selectedTask.taskid}`"
		    endpoint="task-rechtsgrundlage-detail"
		    :logo="rkIconUrl" 
		    :data="selectedRechtsgrundlagen"
		    :columns="rg_cols"
		    :editorPanelColumns="rgReadonlyDetailColumns"
		    :editorReadOnly="true"
		    :enableAdd="false"
		    :enableEdit="false"
		    :enableDetail="true"
		    :enableDelete="true"
		    :enableFilter="false"
		    :enableColChange="false"
		    :alwaysEdit="false"
		    :headerFontSize="12"
		    :singleSelectFontsize="12"
		    :enableColFilterSide="false"
		    :enableColFilterTop="false"
		    :hideActionColumn="false"
		    :onDelete="deleteTaskRechtsgrundlagen"
		    :withpaging="false">
		    <template #logo><ClipboardCheck class="w-8 h-8" /></template>
		  </DataTable>
		  <div class="p-4 space-y-6 bg-white rounded shadow-md w-80">
		    <!-- RG Short Input -->
		    <div class="flex flex-col space-y-1 mt-4">
		      <label for="rg-short-input" class="text-sm font-medium text-gray-700">
		        {{ t('view.rgkuerzel') }}
		      </label>
		      <input
		        v-model="selectedRgShort"
		        type="text"
		        id="rg-short-input"
		        placeholder="Enter value..."
		        class="border border-gray-300 rounded w-full h-10 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
		      />
		    </div>


		    <!-- Submit Button -->
		    <button
		      class="greenbutton mt-4 w-full h-10 text-white font-medium rounded shadow transition-colors disabled:opacity-50"
		      @click="addRgToTask(selectedTask.id)"
		    >
		      <span>{{"Rechtsgrundlage " + t('view.add')}}</span>
		    </button>

		  </div>
		</div>
			  
		<div v-if="showPflichten">
		  <DataTable :label="`Pflichten für ${selectedTask.taskid}`"
		  :logo="rkIconUrl" 
		  :data="selectedPflichten"
		  :columns="pf_cols"
		  :enableAdd="false"
		  :enableEdit="false"
		  :enableDetail="true"
		  :enableDelete="true"
		  :enableFilter="false"
		  :enableColChange="false"
		  :alwaysEdit="false"
		  :headerFontSize="12"
		  :singleSelectFontsize="12"
		  :enableColFilterSide="false"
		  :enableColFilterTop="false"
		  :hideActionColumn="false"
		  :onDelete="deleteTaskPflicht"
		  :withpaging="false">
		  <template #logo><ClipboardCheck class="w-8 h-8" /></template>
		  </DataTable>
		<div class="p-4 space-y-6 bg-white rounded shadow-md w-80">
		    <!-- Rechtsaspekt Select -->
		    <div class="flex flex-col space-y-1">
		      <RechtsaspektSelect
		        ref="rechtsaspektSelect"
		        v-model="selectedRechtsaspekt"
		        id="rechtsaspekt-select"
		        class="w-full"
				rtype="pflichten"
		      />
		    </div>

		    <!-- RG Short Input -->
		    <div class="flex flex-col space-y-1 mt-4">
		      <label for="rg-short-input" class="text-sm font-medium text-gray-700">
		        {{ t('view.rgkuerzel') }}
		      </label>
		      <input
		        v-model="selectedRgShort"
		        type="text"
		        id="rg-short-input"
		        placeholder="Enter value..."
		        class="border border-gray-300 rounded w-full h-10 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
		      />
		    </div>

		    <!-- Referenz Select -->
		    <div class="flex flex-col space-y-1 mt-4">
		      <label for="referenz-select" class="text-sm font-medium text-gray-700 flex items-center gap-2">
		        Referenz
		        <Loader2 v-if="loading" class="w-4 h-4 animate-spin text-blue-500" />
		      </label>
		      <Select
		        v-model="referenzSelect"
		        id="referenz-select"
		        :disabled="!selectedRechtsaspekt || loading"
		        class="w-full"
		      >
		        <SelectTrigger>
		          <SelectValue placeholder="Referenz wählen" />
		        </SelectTrigger>
		        <SelectContent :key="referenzOptions.length" class="z-[10000]">
		          <SelectGroup>
		            <SelectLabel>Filtered Items</SelectLabel>
		            <template v-if="loading">
		              <div class="p-2 text-gray-500 text-sm">Laden...</div>
		            </template>
		            <template v-else-if="referenzOptions.length">
		              <SelectItem
		                v-for="item in referenzOptions"
		                :key="item.value"
		                :value="item.value"
		              >
		                {{ item.label }}
		              </SelectItem>
		            </template>
		            <template v-else>
		              <div class="p-2 text-gray-400 text-sm">No items found</div>
		            </template>
		          </SelectGroup>
		        </SelectContent>
		      </Select>
		    </div>

		    <!-- Submit Button -->
		    <button
		      class="greenbutton mt-4 w-full h-10 text-white font-medium rounded shadow transition-colors disabled:opacity-50"
		      :disabled="!referenzSelect"
		      @click="addPfToTask(selectedTask.id)"
		    >
		      <span>{{"Pflicht " + t('view.add')}}</span>
		    </button>

		  </div>
        </div>
    </template>
  </HorizontalSplitter>	
</template>
